import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import { isAxiosError } from 'axios';
import { auth } from '@/config/firebase';
import apiClient from '@/services/api/apiClient';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { clearJobReferralData } from '@/features/jobs/utils/jobOrigin';

/**
 * Auth service — mirrors the parts of Flutter's IAuthRepository that the web app reaches.
 *
 * Handles all Firebase authentication operations.
 * UI components call these functions; they should NOT call Firebase directly.
 */

// ── Types ────────────────────────────────────────────────────────

export type LoginOutcome =
  | { success: true; user: User }
  | { success: false; message: string };

export type SignUpOutcome =
  | { success: true; user: User }
  | { success: false; message: string; code?: string };

// ── Error mapping ────────────────────────────────────────────────

/** Flutter ErrorConstants.genericError */
export const GENERIC_ERROR = 'Something went wrong, Please try again!';

/** Flutter's Firebase error codes have no 'auth/' prefix (FlutterFire strips it on web). */
function firebaseErrorCode(error: unknown): string {
  return ((error as { code?: string }).code ?? '').replace(/^auth\//, '');
}

/**
 * Maps Firebase error codes to the messages Flutter shows.
 * Mirrors Flutter IAuthRepository.getMessageFromErrorCode(), including its quirks
 * (e.g. 'too-many-requests' isn't listed, so it gets the generic message).
 * 'multi-factor-auth-required' also gets the generic message: with email MFA
 * no Firebase factor is enrolled, and Flutter shows the generic error when it happens.
 */
function getMessageFromErrorCode(code: string): string {
  switch (code) {
    case 'ERROR_EMAIL_ALREADY_IN_USE':
    case 'account-exists-with-different-credential':
    case 'email-already-in-use':
      return 'Email already used. Go to login page.';
    case 'ERROR_WRONG_PASSWORD':
    case 'INVALID_LOGIN_CREDENTIALS':
    case 'wrong-password':
      return 'Wrong password. Use the eye icon to check password';
    case 'ERROR_USER_NOT_FOUND':
    case 'user-not-found':
      return 'User not found';
    case 'ERROR_USER_DISABLED':
    case 'user-disabled':
      return 'User is blocked!';
    case 'ERROR_TOO_MANY_REQUESTS':
    case 'operation-not-allowed':
      return 'Too many requests to log into this account.';
    case 'ERROR_OPERATION_NOT_ALLOWED':
      return 'Server error, please try again later.';
    case 'ERROR_INVALID_EMAIL':
    case 'invalid-email':
      return 'Invalid Email';
    case 'invalid-credential':
      return 'Invalid login credentials';
    default:
      return GENERIC_ERROR;
  }
}

/**
 * Scope Firebase Auth to the current tenant before signing in / up.
 * Mirrors Flutter `_firebaseAuth.tenantId = APIConstants.TENANT_ID` — the reset-password and
 * verify-email links switch auth.tenantId to the link's tenant, so it is re-applied here.
 */
function applyTenantId(): void {
  const workspaceId = useTenantStore.getState().workspaceId;
  if (workspaceId) auth.tenantId = workspaceId;
}

// ── Email/Password Login ─────────────────────────────────────────

/**
 * Email/password login.
 * Mirrors Flutter IAuthRepository.login().
 */
export async function loginWithEmail(
  email: string,
  password: string,
): Promise<LoginOutcome> {
  try {
    applyTenantId();
    const result = await signInWithEmailAndPassword(auth, email, password);
    await result.user.getIdToken(true);
    await result.user.reload();

    // Mirrors LocalDataService.putUserEmailId — /mfa falls back to it
    localStorage.setItem('userEmailId', email);

    return { success: true, user: result.user };
  } catch (error: unknown) {
    return { success: false, message: getMessageFromErrorCode(firebaseErrorCode(error)) };
  }
}

// ── Sign Up with Email/Password ──────────────────────────────────

/**
 * Email/password sign up.
 * Mirrors Flutter IAuthRepository.signUpWithEmailAndPassword().
 */
export async function signUpWithEmail(
  email: string,
  password: string,
): Promise<SignUpOutcome> {
  try {
    applyTenantId();
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await result.user.getIdToken(true);
    await result.user.reload();

    return { success: true, user: result.user };
  } catch (error: unknown) {
    const code = firebaseErrorCode(error);

    if (code.includes('email-already-in-use')) {
      return { success: false, message: 'Email already exists!' };
    }
    if (code.includes('invalid-email')) {
      return { success: false, message: 'Invalid Email' };
    }
    if (code.includes('operation-not-allowed')) {
      return { success: false, message: 'User is blocked!' };
    }
    if (code.includes('weak-password')) {
      return { success: false, message: 'Wrong password. Use the eye icon to check password' };
    }
    if (code.includes('password-does-not-meet-requirements')) {
      return {
        success: false,
        code: 'password-does-not-meet-requirements',
        message: (error as { message?: string }).message ?? '',
      };
    }
    return { success: false, message: GENERIC_ERROR };
  }
}

// ── Account Verify Email ─────────────────────────────────────────

/**
 * Send account verification email via backend.
 * Mirrors Flutter IAuthRepository.accountVerifyEmail() — http + getHeaders(), no interceptor.
 * POST /v1/account/verify/email
 */
export async function accountVerifyEmail(email: string): Promise<boolean> {
  try {
    const response = await apiClient.post('v1/account/verify/email', { email }, {
      skipErrorInterceptor: true,
    });
    return response.status === 200;
  } catch {
    return false;
  }
}

// ── Check Duplicate User ─────────────────────────────────────────

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  isByEmail: boolean;
  isByPhone: boolean;
}

export type DuplicateCheckOutcome =
  | { success: true; result: DuplicateCheckResult }
  | { success: false; message: string };

/**
 * Check if email or mobile number is already registered.
 * Mirrors Flutter IAuthRepository.checkDuplicateUser() — Dio() without interceptor,
 * getHeaders(isNoAuth: true). A 200 or 409 response carries the duplicate details; any other
 * outcome is ErrorConstants.genericError, which stops the sign up (WebSignupBloc._CheckDuplicateUser).
 * POST /v1/p/candidate/checkDuplicity
 */
export async function checkDuplicateUser(params: {
  emailId?: string;
  mobileNumber?: string;
}): Promise<DuplicateCheckOutcome> {
  const requestBody: Record<string, string> = {};
  if (params.emailId) requestBody.emailId = params.emailId;
  if (params.mobileNumber) requestBody.mobileNumber = params.mobileNumber;

  const toOutcome = (data: Partial<DuplicateCheckResult> | undefined): DuplicateCheckOutcome => ({
    success: true,
    result: {
      isDuplicate: data?.isDuplicate ?? false,
      isByEmail: data?.isByEmail ?? false,
      isByPhone: data?.isByPhone ?? false,
    },
  });

  try {
    const response = await apiClient.post<DuplicateCheckResult>(
      'v1/p/candidate/checkDuplicity',
      requestBody,
      { noAuth: true, skipErrorInterceptor: true },
    );
    if (response.status === 200) return toOutcome(response.data);
    return { success: false, message: GENERIC_ERROR };
  } catch (error: unknown) {
    if (isAxiosError<DuplicateCheckResult>(error) && error.response?.status === 409) {
      return toOutcome(error.response.data);
    }
    return { success: false, message: GENERIC_ERROR };
  }
}

// ── Forgot Password ──────────────────────────────────────────────

export type ForgotPasswordOutcome = 'sent' | 'rateLimited' | 'failed';

/**
 * Send forgot password email via backend.
 * Mirrors Flutter IAuthRepository.forgotPasswordEmail() — http + getHeaders(), no interceptor.
 * Only a 200 counts as sent; 429 means the reset-link limit was reached.
 * POST /v1/p/account/forget-password
 */
export async function forgotPasswordEmail(email: string): Promise<ForgotPasswordOutcome> {
  try {
    const response = await apiClient.post('v1/p/account/forget-password', { email }, {
      skipErrorInterceptor: true,
    });
    return response.status === 200 ? 'sent' : 'failed';
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response?.status === 429) return 'rateLimited';
    return 'failed';
  }
}

// ── Logout ───────────────────────────────────────────────────────

/**
 * Logout.
 * Mirrors Flutter IAuthRepository.logout() (revoke refresh tokens + setIsMfaVerified(false))
 * followed by FirebaseAuth.instance.signOut().
 */
export async function logout(): Promise<void> {
  try {
    await revokeRefreshTokens();
  } catch {
    // continue even if revoke fails
  }

  useAuthStore.getState().clearAuth();
  await signOut(auth);
}

/**
 * User-initiated sign out (header menu, after account deletion).
 * Mirrors Flutter header LOGOUT + WebProfileBloc._OnLogout: revoke tokens, clear the MFA
 * session and the job referral data. The cached form schemas are kept, as in Flutter.
 */
export async function signOutUser(): Promise<void> {
  await logout();
  // Flutter LocalDataService.deleteJobRefferalData()
  clearJobReferralData();
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Revoke refresh tokens.
 * Mirrors Flutter IAuthRepository.revokeRefreshTokens() — plain http with only the
 * Authorization and Content-Type headers, so a 401 here must not re-enter the interceptor's logout.
 * POST /v1/firebase/revokeRefreshTokens?uuid=<uid>
 */
async function revokeRefreshTokens(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;

  await apiClient.post(
    `v1/firebase/revokeRefreshTokens?uuid=${encodeURIComponent(user.uid)}`,
    undefined,
    { skipErrorInterceptor: true, authHeaderOnly: true },
  );
}
